import React, { useState, useMemo } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import MilestoneKanban from './MilestoneKanban';
import MilestoneTimeline from './MilestoneTimeline';
import MilestoneAnalytics from './MilestoneAnalytics';

const {
  FiPlus, FiList, FiGrid, FiActivity, FiClock,
  FiFilter, FiSearch,
} = FiIcons;

const PRIORITY_CONFIG = {
  high:   { label: 'High',   color: 'text-red-400',    dot: 'bg-red-400' },
  medium: { label: 'Medium', color: 'text-amber-400',  dot: 'bg-amber-400' },
  low:    { label: 'Low',    color: 'text-zinc-500',   dot: 'bg-zinc-500' },
};

export const isOverdue = (m) =>
  m.status !== 'done' && !m.completed && m.target_date && new Date(m.target_date) < new Date();

const MilestoneTracker = ({ milestones = [], canManage, partnershipId, onToggle, onAdd, onEdit, onDelete, onUpdateMilestone }) => {
  const [view, setView] = useState('list');
  const [search, setSearch] = useState('');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');

  const total     = milestones.length;
  const completed = milestones.filter(m => m.completed || m.status === 'done').length;
  const overdue   = milestones.filter(isOverdue).length;
  const inProg    = milestones.filter(m => m.status === 'in_progress').length;
  const progress  = total > 0 ? Math.round((completed / total) * 100) : 0;

  const categories = useMemo(() => {
    const cats = new Set(milestones.map(m => m.category).filter(Boolean));
    return ['all', ...cats];
  }, [milestones]);

  const filtered = useMemo(() => milestones.filter(m => {
    const matchSearch = !search || m.title?.toLowerCase().includes(search.toLowerCase()) || m.description?.toLowerCase().includes(search.toLowerCase());
    const matchPriority = filterPriority === 'all' || m.priority === filterPriority;
    const matchCategory = filterCategory === 'all' || m.category === filterCategory;
    return matchSearch && matchPriority && matchCategory;
  }), [milestones, search, filterPriority, filterCategory]);

  const VIEWS = [
    { id: 'list',     icon: FiList,     label: 'List' },
    { id: 'kanban',   icon: FiGrid,     label: 'Board' },
    { id: 'timeline', icon: FiClock,    label: 'Timeline' },
    { id: 'analytics',icon: FiActivity, label: 'Analytics' },
  ];

  return (
    <div className="space-y-5">
      {/* Header bar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {VIEWS.map(v => (
            <button key={v.id} onClick={() => setView(v.id)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                view === v.id
                  ? 'bg-gradient-to-r from-brand-pink/20 to-brand-coral/20 border border-brand-pink/30 text-brand-pink'
                  : 'border border-white/5 text-zinc-600 hover:text-zinc-300 hover:border-white/10'
              }`}>
              <SafeIcon icon={v.icon} className="text-xs" />
              <span className="hidden sm:inline">{v.label}</span>
            </button>
          ))}
        </div>
        {canManage && (
          <button onClick={onAdd}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all bg-gradient-to-r from-brand-pink to-brand-coral text-white shadow-[0_0_16px_rgba(255,102,204,0.3)] hover:shadow-[0_0_24px_rgba(255,102,204,0.5)] hover:scale-105 active:scale-95">
            <SafeIcon icon={FiPlus} className="text-xs" /> Add Milestone
          </button>
        )}
      </div>

      {/* Progress bar */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-widest">
          <span className="text-zinc-500">{completed}/{total} complete · {inProg} in progress</span>
          <span className={progress === 100 ? 'text-green-400' : overdue > 0 ? 'text-red-400' : 'text-brand-pink'}>{progress}%</span>
        </div>
        <div className="h-2 rounded-full bg-white/5 overflow-hidden">
          <div className="h-full rounded-full transition-all duration-700 bg-gradient-to-r from-brand-pink to-brand-coral"
            style={{ width: `${progress}%` }} />
        </div>
        {overdue > 0 && (
          <p className="text-[10px] font-black uppercase tracking-widest text-red-400">
            ⚠ {overdue} overdue milestone{overdue > 1 ? 's' : ''}
          </p>
        )}
      </div>

      {/* Filters */}
      {total > 0 && view !== 'analytics' && (
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[160px]">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600 text-xs" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search milestones…"
              className="w-full pl-8 pr-3 py-2 bg-white/5 border border-white/10 rounded-xl text-xs text-white placeholder-zinc-600 focus:outline-none focus:border-brand-pink/40 transition-colors" />
          </div>
          <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)}
            className="px-3 py-2 bg-white/5 border border-white/10 rounded-xl text-[10px] font-black uppercase tracking-widest text-zinc-400 focus:outline-none focus:border-brand-pink/40 transition-colors">
            <option value="all">All Priority</option>
            {['high','medium','low'].map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
            className="px-3 py-2 bg-white/5 border border-white/10 rounded-xl text-[10px] font-black uppercase tracking-widest text-zinc-400 focus:outline-none focus:border-brand-pink/40 transition-colors">
            {categories.map(c => <option key={c} value={c}>{c === 'all' ? 'All Categories' : c}</option>)}
          </select>
        </div>
      )}

      {/* Views */}
      {total === 0 ? (
        <div className="py-12 text-center space-y-3">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-brand-pink/10 to-brand-coral/10 border border-brand-pink/20 flex items-center justify-center">
            <SafeIcon icon={FiGrid} className="text-2xl text-brand-pink/50" />
          </div>
          <p className="text-[10px] font-black uppercase tracking-widest text-zinc-600">No milestones yet</p>
          {canManage && <p className="text-xs font-bold text-zinc-700">Click "Add Milestone" to get started</p>}
        </div>
      ) : view === 'list' ? (
        <MilestoneList milestones={filtered} canManage={canManage} onToggle={onToggle} onEdit={onEdit} onDelete={onDelete} onUpdateMilestone={onUpdateMilestone} />
      ) : view === 'kanban' ? (
        <MilestoneKanban milestones={filtered} canManage={canManage} onToggle={onToggle} onEdit={onEdit} onDelete={onDelete} onUpdateMilestone={onUpdateMilestone} />
      ) : view === 'timeline' ? (
        <MilestoneTimeline milestones={filtered} onEdit={canManage ? onEdit : null} />
      ) : (
        <MilestoneAnalytics milestones={milestones} />
      )}
    </div>
  );
};

/* ─── List View ─────────────────────────────────────────────────────── */
const MilestoneList = ({ milestones, canManage, onToggle, onEdit, onDelete, onUpdateMilestone }) => {
  if (milestones.length === 0)
    return <p className="text-center text-[10px] font-black uppercase tracking-widest text-zinc-700 py-6">No milestones match filters</p>;

  return (
    <div className="space-y-2">
      {milestones.map(m => (
        <MilestoneRow key={m.id} milestone={m} canManage={canManage}
          onToggle={onToggle} onEdit={onEdit} onDelete={onDelete} onUpdateMilestone={onUpdateMilestone} />
      ))}
    </div>
  );
};

/* ─── Milestone Row ─────────────────────────────────────────────────── */
const MilestoneRow = ({ milestone: m, canManage, onToggle, onEdit, onDelete, onUpdateMilestone }) => {
  const [expanded, setExpanded] = useState(false);
  const overdue = isOverdue(m);
  const done = m.completed || m.status === 'done';
  const pCfg = PRIORITY_CONFIG[m.priority] || PRIORITY_CONFIG.medium;

  const STATUS_COLORS = {
    todo: 'text-zinc-500 bg-zinc-800/60 border-zinc-700/40',
    in_progress: 'text-brand-pink bg-brand-pink/10 border-brand-pink/30',
    review: 'text-amber-400 bg-amber-500/10 border-amber-500/30',
    done: 'text-green-400 bg-green-500/10 border-green-500/30',
  };

  return (
    <div className={`rounded-2xl border transition-all ${
      done ? 'border-white/5 bg-white/[0.02]'
      : overdue ? 'border-red-500/20 bg-red-500/5'
      : 'border-white/8 bg-white/[0.04] hover:bg-white/[0.06]'
    }`}>
      <div className="flex items-start gap-3 p-4">
        <button onClick={() => canManage && onToggle(m.id, !done)} disabled={!canManage}
          className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
            done ? 'border-green-500 bg-green-500' : overdue ? 'border-red-400/50' : 'border-zinc-600 hover:border-brand-pink'
          } ${canManage ? 'cursor-pointer' : 'cursor-default'}`}>
          {done && <span className="text-white text-[9px]">✓</span>}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <button onClick={() => setExpanded(e => !e)} className="text-left">
              <p className={`text-sm font-black leading-tight ${done ? 'line-through text-zinc-600' : overdue ? 'text-red-300' : 'text-white'}`}>
                {m.title}
              </p>
            </button>
            <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap">
              <span className={`px-2 py-0.5 rounded-full border text-[9px] font-black uppercase tracking-widest ${STATUS_COLORS[m.status || 'todo']}`}>
                {(m.status || 'todo').replace('_', ' ')}
              </span>
              <span className={`flex items-center gap-1 text-[9px] font-black uppercase tracking-widest ${pCfg.color}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${pCfg.dot}`} />{pCfg.label}
              </span>
              {canManage && (
                <>
                  <button onClick={() => onEdit(m)} className="p-1.5 rounded-lg text-zinc-600 hover:text-brand-pink hover:bg-brand-pink/10 transition-all">
                    <SafeIcon icon={FiIcons.FiEdit2} className="text-xs" />
                  </button>
                  <button onClick={() => onDelete(m.id)} className="p-1.5 rounded-lg text-zinc-600 hover:text-red-400 hover:bg-red-500/10 transition-all">
                    <SafeIcon icon={FiIcons.FiTrash2} className="text-xs" />
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 mt-1.5">
            {m.target_date && (
              <span className={`text-[10px] font-black uppercase tracking-widest ${overdue ? 'text-red-400' : done ? 'text-zinc-700' : 'text-zinc-500'}`}>
                📅 {new Date(m.target_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                {overdue && !done ? ' · Overdue' : ''}
              </span>
            )}
            {m.assignee_label && (
              <span className="text-[10px] font-black uppercase tracking-widest text-zinc-500">
                👤 {m.assignee_label}
              </span>
            )}
            {m.category && m.category !== 'other' && (
              <span className="text-[10px] font-black uppercase tracking-widest text-brand-coral/70">{m.category}</span>
            )}
            {(m.tags || []).slice(0, 3).map(tag => (
              <span key={tag} className="px-1.5 py-0.5 rounded-md bg-white/5 text-[9px] font-black text-zinc-500">#{tag}</span>
            ))}
          </div>

          {/* Progress bar for in-progress */}
          {m.status === 'in_progress' && m.progress_pct > 0 && (
            <div className="mt-2 flex items-center gap-2">
              <div className="flex-1 h-1 rounded-full bg-white/5 overflow-hidden">
                <div className="h-full rounded-full bg-gradient-to-r from-brand-pink to-brand-coral transition-all"
                  style={{ width: `${m.progress_pct}%` }} />
              </div>
              <span className="text-[9px] font-black text-brand-pink">{m.progress_pct}%</span>
            </div>
          )}

          {expanded && m.description && (
            <p className="mt-2 text-xs font-bold text-zinc-400 leading-relaxed">{m.description}</p>
          )}
          {expanded && m.notes && (
            <div className="mt-2 p-3 bg-white/5 rounded-xl border border-white/8">
              <p className="text-xs font-bold text-zinc-400 leading-relaxed">{m.notes}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MilestoneTracker;